import Button from "@components/Button";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { AnalyticsFlowEvent } from "@rapid/analytics/types";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { useKeyboardShortcut } from "@rapid/hooks/useKeyboardShortcut";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { useImproveContext } from "@rapid/contexts/ImproveContext";
import { DeploySteps, Flows } from "@rapid/types/flows";
import {
    useLimitedSources,
    useApprovedImageIds,
    useCurrentMedia,
    useApprovedAnnotationSnapshot,
    useAnnotationsForFile
} from "@rapid/store";

interface ApproveImageBtnProps {
    disabled?: boolean;
    hasScheduledJob?: boolean;
    onRetrain?: () => void | Promise<void>;
}

export const ApproveImageBtn = ({
    disabled = false,
    hasScheduledJob = false,
    onRetrain
}: ApproveImageBtnProps) => {
    const { track } = useAnalytics();
    const { handleApproveImage } = useImproveContext();
    const navigate = useNavigate();
    const { computeUrlPath } = useFlowState();
    const sources = useLimitedSources();
    const approvedImageIds = useApprovedImageIds();
    const currentMedia = useCurrentMedia();

    // Check if the current image has already been approved
    const currentImageId = currentMedia?.file?.id;
    const isCurrentImageApproved = currentImageId ? approvedImageIds.has(currentImageId) : false;

    // Get the stored annotation snapshot for the current image (if approved)
    const approvedSnapshot = useApprovedAnnotationSnapshot(currentImageId);

    // Get current annotations for the current image
    const annotationsForFile = useAnnotationsForFile(currentImageId);

    // Check if annotations have changed since approval
    const hasAnnotationChanges = useMemo(() => {
        // If not approved yet, no need to check for changes
        if (!isCurrentImageApproved || !approvedSnapshot || !currentImageId) {
            return true; // Enable button for unapproved images
        }

        // Get current annotations (for images, frameId = fileId)
        const currentAnnotations = annotationsForFile[currentImageId] || [];

        // Create current snapshot in same format as stored snapshot
        const currentSnapshot = JSON.stringify(
            currentAnnotations.map((a) => ({
                x: a.x,
                y: a.y,
                width: a.width,
                height: a.height,
                label: a.label
            }))
        );

        // Compare snapshots
        return currentSnapshot !== approvedSnapshot;
    }, [isCurrentImageApproved, approvedSnapshot, currentImageId, annotationsForFile]);

    // Check if this is the last unapproved image in the batch
    const isLastUnapproved = useMemo(() => {
        if (!currentImageId || isCurrentImageApproved) return false;
        const unapprovedSources = sources.filter((s) => !approvedImageIds.has(s.id));
        return unapprovedSources.length === 1 && unapprovedSources[0].id === currentImageId;
    }, [currentImageId, isCurrentImageApproved, sources, approvedImageIds]);

    // Disable if no sources, or if already approved with no changes
    const isDisabled =
        disabled || sources.length === 0 || (isCurrentImageApproved && !hasAnnotationChanges);

    const onClickApprove = useCallback(async () => {
        track({
            event: AnalyticsFlowEvent.IMPROVE_IMAGE_APPROVED,
            data: { isUpdate: isCurrentImageApproved }
        });
        await handleApproveImage();
        if (isLastUnapproved) {
            if (hasScheduledJob && onRetrain) {
                onRetrain();
            }
            const deployPath = computeUrlPath({ flow: Flows.DEPLOY, step: DeploySteps.DEPLOY });
            navigate(deployPath);
        }
    }, [
        handleApproveImage,
        track,
        isCurrentImageApproved,
        isLastUnapproved,
        hasScheduledJob,
        onRetrain,
        computeUrlPath,
        navigate
    ]);

    // Keyboard shortcut: Command + Enter
    useKeyboardShortcut({
        key: "Enter",
        handler: onClickApprove,
        disabled: isDisabled,
        requireMeta: true
    });

    // Change button text based on state
    const buttonText = isLastUnapproved
        ? hasScheduledJob
            ? "Approve & Retrain"
            : "Approve and Close"
        : isCurrentImageApproved
          ? "Save Changes"
          : "Approve Image";
    const buttonIcon =
        isLastUnapproved && hasScheduledJob
            ? "far fa-rotate"
            : isCurrentImageApproved
              ? "far fa-save"
              : "far fa-check";

    return (
        <div className="flex flex-col gap-4">
            <Button onClick={onClickApprove} disabled={isDisabled} className="w-full">
                <i className={buttonIcon}></i>
                <span>{buttonText}</span>
                <div className={twClassNames("hotkeysContainer", isDisabled && "opacity-30")}>
                    <span className="hotkey dark-brand">⌘</span>
                    <span className="hotkey dark-brand">⏎</span>
                </div>
            </Button>
        </div>
    );
};
